import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { ArrowLeft, X, Crosshair } from 'lucide-react'
import setThemeColor from '../lib/themeColor'
import GMBountyControls from '../components/GMBountyControls'

import { TIDDisplaySection } from '../components/TIDSection'
import { FUPSection } from '../components/FUPSection'

// ── DOSSIER STYLE TOKENS (from source HTML) ──────────────────
const D = {
  page: {
    minHeight: '100vh',
    background: '#d8c898',
    color: '#1f1d18',
    fontFamily: "'Special Elite', 'Courier Prime', monospace",
  },
  nav: {
    background: '#1f1d18',
    borderBottom: '3px solid #a82a1f',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paper: {
    background: '#f0e6c8',
    border: '1px solid rgba(120,100,60,.35)',
    boxShadow: '0 2px 8px rgba(0,0,0,.18), 0 1px 3px rgba(0,0,0,.12)',
    position: 'relative',
    overflow: 'hidden',
  },
  paperLines: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 26px, rgba(0,0,0,.025) 26px, rgba(0,0,0,.025) 27px)',
  },
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body: { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  mono: { fontFamily: "'Courier Prime', monospace" },
  ink: { color: '#1f1d18' },
  ink2: { color: '#4a4636' },
  ink3: { color: '#7a7058' },
  stamp: { color: '#a82a1f' },
  stampBlue: { color: '#2a4a8c' },
}

const typeEdge = { kill: '#a82a1f', escort: '#2a4a8c', defend: '#6a8a5a' }
const typeLabel = { kill: 'KILL ORDER', escort: 'ESCORT DETAIL', defend: 'DEFEND ASSET', other: 'SPECIAL ORDER' }
const dossierTitle = { kill: 'MISSION DOSSIER', escort: 'ESCORT BRIEFING', defend: 'DEFENSE ORDER', other: 'SPECIAL BRIEFING' }
const classificationFromType = { kill: 'CLASSIFIED', escort: 'RESTRICTED', defend: 'EYES ONLY', other: 'RESTRICTED' }


function Redacted({ width = 48 }) {
  return <span style={{ background: '#1a1a1a', color: 'transparent', display: 'inline-block', minWidth: width, height: '1em', verticalAlign: 'middle', borderRadius: 1 }}>&nbsp;</span>
}

function RedactedPhoto() {
  return (
    <div style={{ width: 72, height: 90, background: '#1a1a1a', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
      <span style={{ ...D.display, fontSize: 7, color: '#444', letterSpacing: 2, transform: 'rotate(-30deg)', position: 'absolute' }}>REDACTED</span>
    </div>
  )
}

function Val({ children, width = 48 }) {
  if (children === null || children === undefined || children === '') return <Redacted width={width} />
  return <span style={D.ink}>{children}</span>
}

function SectionHeader({ num, label }) {
  return (
    <div style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#4a4636', borderBottom: '1px solid #cbb98c', paddingBottom: 3, marginBottom: 8, marginTop: 14 }}>
      § {String(num).padStart(2, '0')} — {label}
    </div>
  )
}

export default function BountyDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [bounty, setBounty] = useState(null)
  const [target, setTarget] = useState(null)
  const [issuer, setIssuer] = useState(null)
  const [event, setEvent] = useState(null)
  const [targetFaction, setTargetFaction] = useState(null)
  const [pursuits, setPursuits] = useState([])
  const [priorBounties, setPriorBounties] = useState([])
  const [myPursuit, setMyPursuit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userFactionId, setUserFactionId] = useState(null)
  const [isGM, setIsGM] = useState(false)
  const [tidHeaderUrl, setTidHeaderUrl] = useState(null)
  const [fupProfileUrl, setFupProfileUrl] = useState(null)

  useEffect(() => { setThemeColor('dossier') }, [])

  useEffect(() => { fetchBounty() }, [id])

  const fetchBounty = async () => {
    setLoading(true)

    // Fetch bounty with target + issuer
    const { data: b } = await supabase
      .from('bounties')
      .select('*, target:users!bounties_target_id_fkey(id, display_name, callsign, photo_url, email), issuer:users!bounties_issued_by_fkey(id, display_name, callsign)')
      .eq('id', id)
      .limit(1)

    const bounty = b?.[0]
    if (!bounty) { navigate('/play'); return }
    setBounty(bounty)
    setTarget(bounty.target)
    setIssuer(bounty.issuer)
    setIsGM(bounty.issued_by === user.id)

    // Fetch event name
    if (bounty.event_id) {
      const { data: ev } = await supabase.from('events').select('id, name, status').eq('id', bounty.event_id).limit(1)
      setEvent(ev?.[0] || null)
    }

    // Fetch target's faction in this event
    if (bounty.event_id && bounty.target_id) {
      const { data: ep } = await supabase
        .from('event_players')
        .select('faction_id, event_role, event_factions(name)')
        .eq('event_id', bounty.event_id)
        .eq('user_id', bounty.target_id)
        .limit(1)
      if (ep?.[0]) {
        setTargetFaction(ep[0].event_factions?.name || null)
      }
    }

    // Fetch pursuits
    const { data: p } = await supabase
      .from('bounty_pursuits')
      .select('*, user:users(display_name, callsign)')
      .eq('bounty_id', id)
    setPursuits(p || [])
    setMyPursuit(p?.find(x => x.user_id === user.id) || null)

    // Fetch prior bounties on same target in same event
    if (bounty.event_id && bounty.target_id) {
      const { data: prior } = await supabase
        .from('bounties')
        .select('id, points, bounty_type, status, issued_at, issuer:users!bounties_issued_by_fkey(display_name, callsign)')
        .eq('event_id', bounty.event_id)
        .eq('target_id', bounty.target_id)
        .neq('id', id)
        .in('status', ['claimed', 'expired'])
        .order('issued_at', { ascending: true })
      setPriorBounties(prior || [])
    }

    // Fetch user's faction for pursuit commits
    const { data: uf } = await supabase
      .from('event_players')
      .select('faction_id')
      .eq('user_id', user.id)
      .order('joined_at', { ascending: false })
      .limit(1)
    setUserFactionId(uf?.[0]?.faction_id || null)

    // ANU-162: most recent T-ID entry for this bounty → header avatar.
    // Query through dossier_entries_view so faction gate is applied automatically.
    const { data: tidRows } = await supabase
      .from('dossier_entries_view')
      .select('id, photo_storage_path, photo_is_redacted')
      .eq('parent_type', 'bounty')
      .eq('parent_id', id)
      .eq('entry_type', 't_id')
      .order('created_at', { ascending: false })
      .limit(1)
    const tidEntry = tidRows?.[0]
    if (tidEntry?.photo_storage_path) {
      const { data: signed } = await supabase.storage
        .from('event-media')
        .createSignedUrl(tidEntry.photo_storage_path, 3600)
      setTidHeaderUrl(signed?.signedUrl || null)
    }

    // ANU-186: most recent OFFICIAL F-UP with update_profile=true overrides T-ID as header avatar.
    const { data: fupProfileRows } = await supabase
      .from('dossier_entries')
      .select('photo_storage_path')
      .eq('parent_type', 'bounty')
      .eq('parent_id', id)
      .eq('entry_type', 'f_up')
      .eq('entry_subtype', 'official')
      .eq('update_profile', true)
      .not('photo_storage_path', 'is', null)
      .order('created_at', { ascending: false })
      .limit(1)
    const fupProfileEntry = fupProfileRows?.[0]
    if (fupProfileEntry?.photo_storage_path) {
      const { data: signed } = await supabase.storage
        .from('event-media')
        .createSignedUrl(fupProfileEntry.photo_storage_path, 3600)
      setFupProfileUrl(signed?.signedUrl || null)
    }

    setLoading(false)
  }

  const commitToBounty = async () => {
    const { error } = await supabase.from('bounty_pursuits').insert({
      bounty_id: id, user_id: user.id, faction_id: userFactionId,
    })
    if (!error) fetchBounty()
  }

  const standDown = async () => {
    if (!myPursuit) return
    await supabase.from('bounty_pursuits').delete().eq('id', myPursuit.id)
    fetchBounty()
  }

  const openScanner = () => {
    // Navigate to player view which has the scanner
    navigate('/play?scan=true')
  }

  if (loading) {
    return (
      <div style={{ ...D.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ ...D.display, fontSize: 14, color: '#7a7058', letterSpacing: '0.14em' }}>RETRIEVING DOSSIER...</p>
      </div>
    )
  }

  if (!bounty) return null

  const classification = classificationFromType[bounty.bounty_type] || 'RESTRICTED'
  const fileNum = `BB-${new Date(bounty.issued_at).getFullYear()}-${bounty.id.slice(0, 4).toUpperCase()}`
  const issuerName = issuer?.callsign || issuer?.display_name || 'Command'
  const targetCallsign = target?.callsign || target?.display_name || 'UNKNOWN'
  const expiresFormatted = bounty.expires_at
    ? new Date(bounty.expires_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  const survivedCount = priorBounties.filter(b => b.status !== 'claimed').length

  let sectionNum = 0
  const sn = () => ++sectionNum

  return (
    <div style={D.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&family=Courier+Prime:wght@400;700&display=swap');`}</style>

      {/* ── NAV ── */}
      <div style={D.nav}>
        <div>
          <div style={{ ...D.display, color: '#f0e6c8', fontSize: 14, letterSpacing: '0.14em' }}>ANUBIS</div>
          <div style={{ color: '#7a7058', fontSize: 9, letterSpacing: '0.12em', marginTop: 2 }}>FIELD OPS · ARCHIVE BRANCH</div>
        </div>
        <div style={{ color: '#7a7058', fontSize: 9, letterSpacing: '0.08em' }}>
          {event?.name?.toUpperCase() || 'NO EVENT'}
        </div>
      </div>

      {/* ── BACK BUTTON ── */}
      <button onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', color: '#7a7058', fontFamily: "'Special Elite', monospace", fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <ArrowLeft size={14} /> RETURN TO FILES
      </button>

      {/* ── DOSSIER PAPER ── */}
      <div style={{ margin: '0 10px 16px', ...D.paper }}>
        <div style={D.paperLines} />
        <div style={{ position: 'relative', zIndex: 1, padding: '20px 16px 24px' }}>

          {/* Header */}
          <div style={{ fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#7a7058', borderBottom: '1px solid #7a7058', paddingBottom: 5, marginBottom: 6 }}>
            DEPT OF FIELD OPERATIONS · ARCHIVE BRANCH
          </div>
          <div style={{ ...D.display, fontSize: 24, fontWeight: 700, letterSpacing: '0.12em', color: '#1f1d18', textTransform: 'uppercase', marginBottom: 3 }}>
            {dossierTitle[bounty.bounty_type] || 'MISSION DOSSIER'}
          </div>
          <div style={{ fontSize: 9, color: '#7a7058', letterSpacing: '0.1em', marginBottom: 16 }}>
            FILE # {fileNum} · OPENED {new Date(bounty.issued_at).toLocaleDateString()} · {event?.name?.toUpperCase() || 'UNASSIGNED'}
          </div>

          {/* Classification stamp */}
          <div style={{ position: 'absolute', top: 44, right: 16, ...D.display, fontSize: 15, fontWeight: 700, color: '#a82a1f', border: '3px solid #a82a1f', padding: '3px 12px', transform: 'rotate(-12deg)', opacity: 0.5, letterSpacing: '0.2em', pointerEvents: 'none' }}>
            {classification}
          </div>

          {/* ── TARGET CARD ── */}
          <div style={{ background: '#e8dec3', border: '1px solid rgba(120,100,60,.35)', padding: 14, marginBottom: 16, display: 'flex', gap: 14 }}>
            {(fupProfileUrl || tidHeaderUrl) ? (
              <div style={{ width: 72, height: 90, border: '1px solid #7a7058', flexShrink: 0, overflow: 'hidden' }}>
                <img src={fupProfileUrl || tidHeaderUrl} alt={targetCallsign} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : (
              <RedactedPhoto />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ ...D.display, fontSize: 17, fontWeight: 700, letterSpacing: '0.08em', display: 'block', marginBottom: 5 }}>
                {targetCallsign}
              </span>
              <div style={{ display: 'flex', gap: 6, marginBottom: 2, fontSize: 11, lineHeight: 1.5, alignItems: 'center' }}>
                <span style={{ ...D.ink3, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9, minWidth: 42, flexShrink: 0 }}>UNIT</span>
                <Val width={80}>{targetFaction}</Val>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 2, fontSize: 11, lineHeight: 1.5, alignItems: 'center' }}>
                <span style={{ ...D.ink3, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9, minWidth: 42, flexShrink: 0 }}>ALIAS</span>
                <Val width={70}>{target?.callsign && target?.callsign !== target?.display_name ? `"${target.callsign}"` : null}</Val>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 2, fontSize: 11, lineHeight: 1.5, alignItems: 'center' }}>
                <span style={{ ...D.ink3, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9, minWidth: 42, flexShrink: 0 }}>SEEN</span>
                <Redacted width={100} />
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 2, fontSize: 11, lineHeight: 1.5, alignItems: 'center' }}>
                <span style={{ ...D.ink3, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9, minWidth: 42, flexShrink: 0 }}>LOAD</span>
                <Redacted width={90} />
              </div>
              <div style={{ ...D.display, fontSize: 13, color: '#a82a1f', marginTop: 5, letterSpacing: '0.08em' }}>
                BNTY ★ {bounty.points.toLocaleString()} PTS
              </div>
            </div>
          </div>

          {/* ── TITLE ── */}
          {bounty.title && (
            <>
              <SectionHeader num={sn()} label="TITLE" />
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', color: '#1f1d18', marginBottom: 4 }}>
                {bounty.title}
              </div>
            </>
          )}

          {/* ── MISSION NOTES ── */}
          {bounty.notes && (
            <>
              <SectionHeader num={sn()} label="MISSION NOTES" />
              <div style={{ fontSize: 12, lineHeight: 1.65, color: '#4a4636' }}>
                {bounty.notes}
              </div>
            </>
          )}

          {/* ── PRIOR BOUNTY HISTORY ── */}
          <SectionHeader num={sn()} label="PRIOR BOUNTY HISTORY" />
          {priorBounties.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr>
                    {['ISSUED', 'TYPE', 'BY', 'OUTCOME', 'PTS'].map(h => (
                      <th key={h} style={{ textAlign: h === 'PTS' ? 'right' : 'left', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#7a7058', borderBottom: '1px solid #7a7058', padding: '3px 6px 3px 0', fontWeight: 400 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {priorBounties.map(pb => {
                    const outcomeStyle = {
                      claimed: { background: 'rgba(168,42,31,.12)', color: '#a82a1f', border: '1px solid rgba(168,42,31,.3)' },
                      expired: { background: 'rgba(0,0,0,.04)', color: '#7a7058', border: '1px solid rgba(0,0,0,.1)' },
                      cancelled: { background: 'rgba(42,74,140,.08)', color: '#2a4a8c', border: '1px solid rgba(42,74,140,.2)' },
                      active: { background: 'rgba(106,138,90,.08)', color: '#6a8a5a', border: '1px solid rgba(106,138,90,.2)' },
                    }
                    return (
                      <tr key={pb.id}>
                        <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c', color: '#4a4636' }}>
                          {new Date(pb.issued_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                        </td>
                        <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c', color: '#4a4636', textTransform: 'uppercase' }}>{pb.bounty_type}</td>
                        <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c', color: '#4a4636' }}>
                          {pb.issuer?.callsign || pb.issuer?.display_name || 'GM'}
                        </td>
                        <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c' }}>
                          <span style={{ ...D.display, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '1px 5px', ...(outcomeStyle[pb.status] || outcomeStyle.expired) }}>
                            {pb.status}
                          </span>
                        </td>
                        <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c', textAlign: 'right', ...D.display, fontSize: 11, color: '#a82a1f' }}>{pb.points}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {survivedCount > 0 && (
                <div style={{ ...D.display, fontSize: 10, color: '#a82a1f', marginTop: 6, letterSpacing: '0.08em' }}>
                  ▲ TARGET HAS EVADED {survivedCount} PRIOR {survivedCount === 1 ? 'BOUNTY' : 'BOUNTIES'}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, color: '#7a7058', fontStyle: 'italic' }}>
              No prior bounties on file for this target.
            </div>
          )}

          {/* ── TARGET IDENTIFICATION (ANU-88) ── */}
          <TIDDisplaySection
            parentType="bounty"
            parentId={bounty.id}
            eventId={bounty.event_id}
            sectionNum={sn()}
          />

          {/* ── FIELD UPDATES (ANU-132) ── */}
          <FUPSection
            parentType="bounty"
            parentId={bounty.id}
            eventId={bounty.event_id}
            sectionNum={sn()}
          />

          {/* ── PURSUIT LEDGER ── */}
          <SectionHeader num={sn()} label="PURSUIT LEDGER" />
          {pursuits.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
              <thead>
                <tr>
                  {['OPERATIVE', 'SINCE'].map(h => (
                    <th key={h} style={{ textAlign: 'left', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#7a7058', borderBottom: '1px solid #7a7058', padding: '3px 6px 3px 0', fontWeight: 400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pursuits.map(p => (
                  <tr key={p.id}>
                    <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c', color: '#4a4636' }}>
                      {p.user?.callsign || p.user?.display_name || 'Unknown'}
                    </td>
                    <td style={{ padding: '4px 6px 4px 0', borderBottom: '1px solid #cbb98c', color: '#7a7058' }}>
                      {new Date(p.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} style={{ padding: '4px 6px 4px 0', fontWeight: 700, color: '#1f1d18', borderBottom: '1px solid #cbb98c' }}>
                    OPEN · {targetCallsign} · {typeLabel[bounty.bounty_type]}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: '#7a7058', fontStyle: 'italic' }}>
              No operatives currently pursuing. Bounty is unclaimed.
            </div>
          )}

          {/* ── ROUTING SLIP ── */}
          <div style={{ background: '#e8dec3', border: '1px dashed #7a7058', padding: '8px 12px', fontSize: 9, color: '#7a7058', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 14 }}>
            <div style={{ fontSize: 7, letterSpacing: '0.2em', marginBottom: 4, opacity: 0.5 }}>UNAUTHORIZED REPRODUCTION PROHIBITED</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
              <span>FROM: {issuerName}</span>
              <span>OPENED: {new Date(bounty.issued_at).toLocaleDateString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>EVENT: {event?.name?.toUpperCase() || 'NONE'}</span>
              <span>{expiresFormatted ? `EXPIRES: ${expiresFormatted}` : 'NO EXPIRY'}</span>
            </div>
          </div>

          {/* ── GM CONTROLS ── */}
          {isGM && (bounty.status === 'active' || bounty.status === 'paused') && (
            <GMBountyControls bounty={bounty} userId={user.id} onUpdate={fetchBounty} />
          )}

          {/* ── ACTIONS ── */}
          {bounty.status === 'active' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid #cbb98c' }}>
              {myPursuit ? (
                <button onClick={standDown}
                  style={{ flex: 1, padding: '11px 14px', ...D.display, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', background: '#a82a1f', color: '#f0e6c8' }}>
                  ✕ STAND DOWN
                </button>
              ) : (
                <button onClick={commitToBounty}
                  style={{ flex: 1, padding: '11px 14px', ...D.display, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', background: '#1f1d18', color: '#f0e6c8' }}>
                  ◎ PURSUE {bounty.bounty_type === 'escort' || bounty.bounty_type === 'defend' ? 'MISSION' : 'TARGET'}
                </button>
              )}
            </div>
          )}

          {bounty.status !== 'active' && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #cbb98c', textAlign: 'center' }}>
              <span style={{ ...D.display, fontSize: 14, letterSpacing: '0.14em', color: bounty.status === 'claimed' ? '#a82a1f' : '#7a7058' }}>
                BOUNTY {bounty.status.toUpperCase()}
              </span>
            </div>
          )}

          {/* Page number */}
          <div style={{ textAlign: 'right', fontSize: 8, color: '#7a7058', letterSpacing: '0.08em', marginTop: 10, paddingTop: 6, borderTop: '1px solid #cbb98c' }}>
            PG 01 / 01 · ORIGIN REPRODUCTION PROHIBITED
          </div>
        </div>
      </div>
    </div>
  )
}
